import React, { useState } from 'react';
import { User as UserIcon, Info, AlertCircle, CheckCircle2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription
} from '@/components/ui/dialog';
import config from '@/config';
import { apiFetch } from '@/utils/api';

interface GenderOnboardingProps {
  isOpen: boolean;
  onComplete: () => void;
}

const GENDER_OPTIONS = [
  { value: 'male', label: 'Male', icon: '♂️' },
  { value: 'female', label: 'Female', icon: '♀️' }
];

export function GenderOnboarding({ isOpen, onComplete }: GenderOnboardingProps) {
  const { getIdToken } = useAuth();
  const [selectedGender, setSelectedGender] = useState<string>('');
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const handleGenderSubmit = async () => {
    if (!selectedGender) {
      setError('Please select a gender to continue');
      return;
    }

    setIsUpdating(true);
    setError(null);
    setSuccess(false);

    try {
      const token = getIdToken();
      if (!token) {
        setError('Authentication required. Please login again.');
        return;
      }

      const response = await apiFetch(`${config.baseUrl}/${config.endpoints.updateGender}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({ gender: selectedGender })
      });

      const data = await response.json();

      if (!response.ok) {
        setError(data.message || 'Failed to set gender. Please try again.');
        return;
      }

      // Success
      setSuccess(true);
      setTimeout(() => {
        // Refresh to get updated user data
        window.location.reload();
      }, 1000);
    } catch (err) {
      console.error('Error setting gender:', err);
      setError('An error occurred. Please try again.');
    } finally {
      setIsUpdating(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={() => {}}>
      <DialogContent
        className="bg-[#1C2431] border border-slate-700 text-white sm:max-w-md [&>button]:hidden"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <DialogHeader>
          <div className="flex items-center justify-center gap-2 mb-2">
            <UserIcon className="w-6 h-6 text-purple-400" />
            <DialogTitle className="text-white text-xl">Welcome!</DialogTitle>
          </div>
          <DialogDescription className="text-slate-300 text-center">
            Before you start using video chat, please select your gender.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 mt-4">
          {/* Gender Selection */}
          <div>
            <label className="text-white text-sm font-medium mb-3 block text-center">
              Select Your Gender
            </label>
            <div className="grid grid-cols-2 gap-3">
              {GENDER_OPTIONS.map((option) => (
                <button
                  key={option.value}
                  onClick={() => {
                    setSelectedGender(option.value);
                    setError(null);
                    setSuccess(false);
                  }}
                  disabled={isUpdating || success}
                  className={`p-4 rounded-lg text-sm font-medium transition-all border-2 ${
                    selectedGender === option.value
                      ? 'bg-purple-600 border-purple-500 text-white shadow-lg shadow-purple-500/50'
                      : 'bg-[#0E1525] border-slate-600 text-slate-300 hover:bg-[#1C2431] hover:border-purple-400 hover:text-white'
                  } ${(isUpdating || success) ? 'opacity-50 cursor-not-allowed' : ''}`}
                >
                  <div className="flex flex-col items-center justify-center gap-2">
                    <span className="text-2xl">{option.icon}</span>
                    <span>{option.label}</span>
                  </div>
                </button>
              ))}
            </div>
          </div>

          {/* Info Message */}
          <div className="p-3 bg-blue-900/20 border border-blue-500/30 rounded-lg">
            <div className="flex items-start gap-2">
              <Info className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
              <p className="text-slate-300 text-xs">
                You can update your gender later in settings, but changes are limited to once every 14 days.
              </p>
            </div>
          </div>

          {/* Error Message */}
          {error && (
            <div className="p-3 bg-red-900/20 border border-red-500/30 rounded-lg">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                <p className="text-red-300 text-sm">{error}</p>
              </div>
            </div>
          )}

          {/* Success Message */}
          {success && (
            <div className="p-3 bg-green-900/20 border border-green-500/30 rounded-lg">
              <div className="flex items-start gap-2">
                <CheckCircle2 className="w-4 h-4 text-green-400 mt-0.5" />
                <p className="text-green-300 text-sm">Gender set successfully! Redirecting...</p>
              </div>
            </div>
          )}

          {/* Continue Button */}
          <Button
            onClick={handleGenderSubmit}
            disabled={!selectedGender || isUpdating || success}
            className="w-full bg-purple-600 hover:bg-purple-700 text-white disabled:opacity-50 disabled:cursor-not-allowed py-6 text-base font-semibold"
          >
            {isUpdating ? 'Setting Gender...' : success ? 'Redirecting...' : 'Continue to Video Chat'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
